import { KBD } from "@/components/ui/KBD";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface PaletteItem {
  id: string;
  label: string;
  path: string;
}

const ALL_ROUTES: PaletteItem[] = [
  { id: "home", label: "Home", path: "/" },
  { id: "playground", label: "SDK Playground", path: "/playground" },
  { id: "activity", label: "Activity Feed", path: "/activity" },
  { id: "issues", label: "Good First Issues", path: "/issues" },
  { id: "changelog", label: "Changelog", path: "/changelog" },
  { id: "docs", label: "API Docs", path: "/docs" },
  { id: "contributors", label: "Contributors", path: "/contributors" },
  { id: "search", label: "Search", path: "/search" },
  { id: "settings", label: "Settings", path: "/settings" },
  { id: "notifications", label: "Notifications", path: "/notifications" },
  { id: "community", label: "Community", path: "/community" },
  { id: "api-docs", label: "REST API Reference", path: "/api-docs" },
];

/**
 * Ctrl+K / Cmd+K command palette for quick navigation.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const filtered = ALL_ROUTES.filter((r) =>
    r.label.toLowerCase().includes(query.toLowerCase()),
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const handleSelect = (item: PaletteItem) => {
    setOpen(false);
    navigate(item.path);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIndex]) handleSelect(filtered[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[20vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      {/* Panel */}
      <div
        className="relative w-full max-w-md rounded-xl border border-border bg-bg-card shadow-2xl overflow-hidden"
      >
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={true}
          aria-controls="command-palette-options"
          aria-haspopup="listbox"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type to navigate..."
          className="w-full px-4 py-3 bg-transparent border-b border-border text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
          aria-label="Command palette search"
        />
        <ul id="command-palette-options" role="listbox" className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <li className="px-4 py-3 text-sm text-text-muted">No results</li>
          )}
          {filtered.map((item, i) => (
            <li
              key={item.id}
              role="option"
              aria-selected={i === activeIndex}
              tabIndex={0}
              onClick={() => handleSelect(item)}
              onKeyDown={(e) => {
                handleKeyDown(e);
                if (e.key === "Enter" || e.key === " ") handleSelect(item);
              }}
              className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                i === activeIndex
                  ? "bg-primary/10 text-primary"
                  : "text-text-muted hover:bg-bg-surface"
              }`}
            >
              <span className="font-medium">{item.label}</span>
              <span className="ml-2 text-xs opacity-60">{item.path}</span>
            </li>
          ))}
        </ul>
        <div className="px-4 py-2 border-t border-border text-[10px] text-text-muted flex items-center gap-3">
          <span>
            <KBD>↑</KBD>
            <KBD>↓</KBD> navigate
          </span>
          <span>
            <KBD>↵</KBD> select
          </span>
          <span>
            <KBD>Esc</KBD> close
          </span>
        </div>
      </div>
    </div>
  );
}
